import { Component, Fragment, type ErrorInfo, type ReactNode } from "react";

type Props = { children: ReactNode };
type State = { hasError: boolean; resetKey: number };

function isTranslateRemoveChildError(error: unknown): boolean {
  const name = error instanceof Error ? error.name : "";
  const message = error instanceof Error ? error.message : String(error ?? "");
  return name === "NotFoundError" || message.includes("removeChild");
}

/**
 * Un toast/flushSync/removeChild no debe dejar #root vacío (pantalla negra).
 * Chrome Translate muta spans de Radix y dispara NotFoundError/removeChild:
 * se ignora y se remonta el árbol, sin pantalla de crash.
 */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, resetKey: 0 };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    if (isTranslateRemoveChildError(error)) {
      return { hasError: false };
    }
    return { hasError: true };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    if (isTranslateRemoveChildError(error)) {
      this.setState((s) => ({ hasError: false, resetKey: s.resetKey + 1 }));
      return;
    }
    console.error("AppErrorBoundary:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-6 text-foreground">
          <h1 className="text-lg font-semibold">No se pudo mostrar la pantalla</h1>
          <p className="max-w-md text-center text-sm text-muted-foreground">
            Ocurrió un error inesperado. Recargá la página para continuar.
          </p>
          <button
            type="button"
            className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground"
            onClick={() => window.location.reload()}
          >
            Recargar
          </button>
        </div>
      );
    }
    return <Fragment key={this.state.resetKey}>{this.props.children}</Fragment>;
  }
}
